import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetPipedriveOwnersQuery,
	useGetPipedriveUserMappingsQuery,
	useCreatePipedriveUserMappingMutation,
	useDeletePipedriveUserMappingMutation,
} from "@/pages/Private/redux/pipedrive/pipedrive.api";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

interface LocalMapping {
	userId: number;
	pipedriveOwnerId: string;
	pipedriveOwnerName?: string;
}

export const PipedriveUserMappingTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.pipedrive.userMapping.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: sendersData } = useGetSendersUsersQuery();
	const { data: ownersData } = useGetPipedriveOwnersQuery();
	const { data: userMappingsData, isLoading: isLoadingMappings } =
		useGetPipedriveUserMappingsQuery();
	const [createUserMapping] = useCreatePipedriveUserMappingMutation();
	const [deleteUserMapping] = useDeletePipedriveUserMappingMutation();

	const [mappings, setMappings] = useState<LocalMapping[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (userMappingsData) {
			setMappings(
				userMappingsData.map((m) => ({
					userId: m.userId,
					pipedriveOwnerId: m.pipedriveOwnerId,
					pipedriveOwnerName: m.pipedriveOwnerName,
				}))
			);
		}
	}, [userMappingsData]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => ({
				id: o.id,
				title: o.name ? `${o.name} (${o.email})` : o.email,
			})) || [],
		[ownersData]
	);

	const handleOwnerChange = (userId: number, ownerId: string) => {
		const owner = ownersData?.owners?.find((o) => o.id === ownerId);

		setMappings((prev) => {
			const existing = prev.find((m) => m.userId === userId);

			if (existing) {
				return prev.map((m) =>
					m.userId === userId
						? { ...m, pipedriveOwnerId: ownerId, pipedriveOwnerName: owner?.name }
						: m
				);
			}

			return [...prev, { userId, pipedriveOwnerId: ownerId, pipedriveOwnerName: owner?.name }];
		});
	};

	const handleSave = async () => {
		try {
			setIsSaving(true);

			const serverMappings = userMappingsData || [];

			const toDelete = serverMappings.filter((sm) => {
				const local = mappings.find((m) => m.userId === sm.userId);

				return !local || local.pipedriveOwnerId !== sm.pipedriveOwnerId;
			});

			const toCreate = mappings.filter((m) => {
				const server = serverMappings.find((sm) => sm.userId === m.userId);

				return !server || server.pipedriveOwnerId !== m.pipedriveOwnerId;
			});

			await Promise.all(toDelete.map((m) => deleteUserMapping(m.id).unwrap()));

			await Promise.all(
				toCreate.map((m) =>
					createUserMapping({
						userId: m.userId,
						pipedriveOwnerId: m.pipedriveOwnerId,
						pipedriveOwnerName: m.pipedriveOwnerName,
					}).unwrap()
				)
			);

			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		} finally {
			setIsSaving(false);
		}
	};

	const senders = sendersData?.data || [];

	if (isLoadingMappings) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta mb-1">{ts("description")}</div>

			{senders.length === 0 ? (
				<div className="py-6 text-center text-text-meta">{ts("noUsers")}</div>
			) : (
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-4 py-2 border-b border-border-light font-medium text-sm text-text-meta">
						<div className="flex-1">{ts("signalistUser")}</div>
						<div className="flex-1">{ts("pipedriveOwner")}</div>
					</div>

					{senders.map((user) => {
						const currentMapping = mappings.find((m) => m.userId === user.id);
						const defaultOwner = currentMapping
							? ownerOptions.find((o) => o.id === currentMapping.pipedriveOwnerId)
							: undefined;

						return (
							<div
								key={user.id}
								className="flex items-center gap-4 py-2 border-b border-border-light"
							>
								<div className="flex-1 text-sm text-text">
									{user.firstName} {user.lastName} ({user.email})
								</div>
								<div className="flex-1">
									<Dropdown
										data={ownerOptions}
										defaultValue={defaultOwner}
										floating={true}
										handleSelect={(value) => {
											handleOwnerChange(user.id, value.id as string);
										}}
										label=""
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
